import React, { useState, useEffect, createContext, useContext } from 'react';
import { ToastProps, ToastMessage, ToastContextType } from '../../../types/uiSpec';

/**
 * Toast - Universal Notification System
 *
 * Generic toast notification for user feedback on actions.
 * Works across all domains for success/error/info/warning messages.
 *
 * Features:
 * - Multiple toast types (success, error, info, warning)
 * - Auto-dismiss with configurable timeout
 * - Manual dismissal
 * - Stacked toast display
 * - Slide-in/fade-out animations
 * - Position options (top-right, top-center, bottom-right, bottom-center)
 * - Dark mode support
 * - Icon indicators
 *
 * Universal Use Cases:
 * - ADU: "Budget saved!", "Upload complete", "Permit submitted"
 * - Veterans Benefits: "Claim filed", "Document uploaded", "Eligibility verified"
 * - Finance: "Payment processed", "Transfer complete", "Account updated"
 * - Any domain: User action feedback, async operation results
 *
 * Usage:
 * ```tsx
 * const { showToast } = useToast();
 * showToast({ type: 'success', message: 'Saved!', duration: 3000 });
 * ```
 */

// Toast Context for global toast management
const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
 const context = useContext(ToastContext);
 if (!context) {
 throw new Error('useToast must be used within ToastProvider');
 }
 return context;
};

// Toast Provider Component
export const ToastProvider: React.FC<{ children: React.ReactNode; position?: 'top-right' | 'top-center' | 'bottom-right' | 'bottom-center' }> = ({
 children,
 position = 'top-right',
}) => {
 const [toasts, setToasts] = useState<ToastMessage[]>([]);

 const showToast = (toast: Omit<ToastMessage, 'id'>) => {
 const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
 const newToast: ToastMessage = {
 ...toast,
 id,
 duration: toast.duration ?? 3000,
 };
 setToasts(prev => [...prev, newToast]);
 };

 const hideToast = (id: string) => {
 setToasts(prev => prev.filter(t => t.id !== id));
 };

 const positionClasses = {
 'top-right': 'top-4 right-4',
 'top-center': 'top-4 left-1/2 -translate-x-1/2',
 'bottom-right': 'bottom-4 right-4',
 'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
 };

 return (
 <ToastContext.Provider value={{ showToast, hideToast }}>
 {children}
 <div className={`fixed ${positionClasses[position]} z-50 flex flex-col gap-2 pointer-events-none`}>
 {toasts.map(toast => (
 <ToastItem key={toast.id} toast={toast} onDismiss={hideToast} />
 ))}
 </div>
 </ToastContext.Provider>
 );
};

// Individual Toast Item Component
const ToastItem: React.FC<{ toast: ToastMessage; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
 const [isExiting, setIsExiting] = useState(false);

 useEffect(() => {
 if (toast.duration && toast.duration > 0) {
 const timer = setTimeout(() => {
 handleDismiss();
 }, toast.duration);

 return () => clearTimeout(timer);
 }
 }, [toast.duration]);

 const handleDismiss = () => {
 setIsExiting(true);
 setTimeout(() => {
 onDismiss(toast.id);
 }, 300); // Animation duration
 };

 const typeStyles = {
 success: {
 bg: 'bg-[var(--orca-success)]/10 border-[var(--orca-success)]',
 text: 'text-green-900',
 icon: '✓',
 iconBg: 'bg-[var(--orca-success)]',
 },
 error: {
 bg: 'bg-[var(--orca-error)]/10 border-[var(--orca-error)]',
 text: 'text-red-900',
 icon: '✕',
 iconBg: 'bg-[var(--orca-error)]',
 },
 warning: {
 bg: 'bg-yellow-50 border-[var(--orca-warning)]',
 text: 'text-yellow-900',
 icon: '⚠',
 iconBg: 'bg-[var(--orca-warning)]',
 },
 info: {
 bg: 'bg-[var(--orca-accent-soft)] border-blue-500',
 text: 'text-blue-900',
 icon: 'ℹ',
 iconBg: 'bg-[var(--orca-accent)]',
 },
 };

 const style = typeStyles[toast.type];

 return (
 <div
 className={`
 ${style.bg} ${style.text} border-l-4 ${style.bg.split(' ')[2]}
 px-4 py-3 rounded-[var(--orca-radius-md)] shadow-lg max-w-md pointer-events-auto
 flex items-center gap-3 transition-all duration-300
 ${isExiting ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}
 `}
 role="alert"
 >
 {/* Icon */}
 <div className={`w-6 h-6 ${style.iconBg} rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
 {style.icon}
 </div>

 {/* Message */}
 <div className="flex-1 min-w-0">
 {toast.title && (
 <p className="font-semibold text-sm">{toast.title}</p>
 )}
 <p className={`text-sm ${toast.title ? 'mt-0.5' : ''}`}>{toast.message}</p>
 </div>

 {/* Dismiss Button */}
 <button
 type="button"
 onClick={handleDismiss}
 className="flex-shrink-0 w-6 h-6 rounded-full hover:bg-black hover:bg-opacity-10 flex items-center justify-center transition-colors"
 aria-label="Dismiss notification"
 >
 <span className="text-xs">✕</span>
 </button>
 </div>
 );
};

// Standalone Toast Component (for manual usage without context)
const Toast: React.FC<ToastProps> = ({ type, title, message, onDismiss, duration = 3000 }) => {
 const [isVisible, setIsVisible] = useState(true);

 useEffect(() => {
 if (duration > 0) {
 const timer = setTimeout(() => {
 setIsVisible(false);
 setTimeout(() => onDismiss?.(), 300);
 }, duration);

 return () => clearTimeout(timer);
 }
 }, [duration, onDismiss]);

 if (!isVisible) return null;

 const typeStyles = {
 success: {
 bg: 'bg-[var(--orca-success)]/10 border-[var(--orca-success)]',
 text: 'text-green-900',
 icon: '✓',
 iconBg: 'bg-[var(--orca-success)]',
 },
 error: {
 bg: 'bg-[var(--orca-error)]/10 border-[var(--orca-error)]',
 text: 'text-red-900',
 icon: '✕',
 iconBg: 'bg-[var(--orca-error)]',
 },
 warning: {
 bg: 'bg-yellow-50 border-[var(--orca-warning)]',
 text: 'text-yellow-900',
 icon: '⚠',
 iconBg: 'bg-[var(--orca-warning)]',
 },
 info: {
 bg: 'bg-[var(--orca-accent-soft)] border-blue-500',
 text: 'text-blue-900',
 icon: 'ℹ',
 iconBg: 'bg-[var(--orca-accent)]',
 },
 };

 const style = typeStyles[type];

 return (
 <div
 className={`
 ${style.bg} ${style.text} border-l-4 ${style.bg.split(' ')[2]}
 px-4 py-3 rounded-[var(--orca-radius-md)] shadow-lg max-w-md
 flex items-center gap-3 transition-opacity duration-300
 `}
 role="alert"
 >
 {/* Icon */}
 <div className={`w-6 h-6 ${style.iconBg} rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
 {style.icon}
 </div>

 {/* Message */}
 <div className="flex-1 min-w-0">
 {title && (
 <p className="font-semibold text-sm">{title}</p>
 )}
 <p className={`text-sm ${title ? 'mt-0.5' : ''}`}>{message}</p>
 </div>

 {/* Dismiss Button */}
 {onDismiss && (
 <button
 type="button"
 onClick={() => {
 setIsVisible(false);
 setTimeout(onDismiss, 300);
 }}
 className="flex-shrink-0 w-6 h-6 rounded-full hover:bg-black hover:bg-opacity-10 flex items-center justify-center transition-colors"
 aria-label="Dismiss notification"
 >
 <span className="text-xs">✕</span>
 </button>
 )}
 </div>
 );
};

export default Toast;
